import { ReactElement, ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

/**
 * Keyboard layer for the back office (#2132, wave 3).
 *
 * The system had two shortcuts, both Ctrl+Enter to submit, and six further key
 * handlers whose whole job was to suppress input. This is the first layer the
 * study asks for: single letters for the handful of moves that happen hundreds
 * of times a day, the way mail clients do it.
 *
 * A single letter must never steal a keystroke from someone who is typing, so
 * plain keys are ignored while an editable element has the focus. Combinations
 * with a modifier are not: ⌘S is meant to work in the middle of a form.
 */

export interface PaloShortcut {
    /**
     * The key as KeyboardEvent.key produces it, e.g. "j" or "Enter".
     *
     * Not called `key`: a shortcut is handed to PaloShortcutBinding as props,
     * and React swallows a prop by that name — the shortcut would silently
     * never register.
     */
    combo: string;
    /** Cmd on mac, Ctrl elsewhere. */
    meta?: boolean;
    shift?: boolean;
    /** What it does, for the overlay behind "?". */
    label: ReactNode;
    /** Grouping in the overlay, e.g. "Liste" or "Beleg". */
    group?: ReactNode;
    handler: (event: KeyboardEvent) => void;
    /** Registered but inert — stays visible in the overlay, greyed out. */
    disabled?: boolean;
    /**
     * Active, but not worth listing: Esc-to-close is self-evident and every
     * layer that can be closed would otherwise add a line of its own.
     */
    hidden?: boolean;
}

interface ShortcutRegistry {
    register: (shortcut: PaloShortcut) => () => void;
    /** Everything active right now, for the overlay. */
    list: () => PaloShortcut[];
}

const PaloShortcutContext = createContext<ShortcutRegistry | undefined>(undefined);

const isMac = (): boolean =>
    'undefined' !== typeof navigator && /mac|iphone|ipad/i.test(navigator.userAgent);

/**
 * How a shortcut is written down for the reader.
 *
 * The matcher takes Cmd and Ctrl alike, so the same registration works on every
 * platform — but the label must not. A Windows user reading "⌘K" is told about
 * a key their keyboard does not have. One function, so the topbar chip and the
 * "?" overlay cannot drift apart.
 */
export const paloShortcutLabel = (combo: string, meta?: boolean, shift?: boolean): string => {
    const parts: string[] = [];

    if (meta) {
        parts.push(isMac() ? '⌘' : 'Strg+');
    }

    if (shift) {
        parts.push(isMac() ? '⇧' : 'Umschalt+');
    }

    parts.push(1 === combo.length ? combo.toUpperCase() : combo);

    return parts.join('');
};

const isEditable = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) {
        return false;
    }

    if (target.isContentEditable) {
        return true;
    }

    const tag = target.tagName;

    return 'INPUT' === tag || 'TEXTAREA' === tag || 'SELECT' === tag;
};

const matches = (shortcut: PaloShortcut, event: KeyboardEvent): boolean => {
    const wantsMeta = shortcut.meta ?? false;
    // Cmd on mac, Ctrl everywhere else — the same shortcut to the hand.
    const hasMeta = event.metaKey || event.ctrlKey;

    if (wantsMeta !== hasMeta) {
        return false;
    }

    if (wantsMeta) {
        // Held with a modifier the browser still reports the base key, so the
        // comparison stays loose and an explicit shift is honoured.
        return shortcut.combo.toLowerCase() === event.key.toLowerCase() && (shortcut.shift ?? false) === event.shiftKey;
    }

    // On its own, the character the key produced IS the contract. Comparing
    // the shift flag on top of it makes every character that needs Shift
    // unreachable: "?" is Shift+ß on a German keyboard, so the help overlay
    // could never open — the key it is bound to cannot be typed without the
    // flag the matcher insisted on not seeing.
    if (1 === shortcut.combo.length) {
        return shortcut.combo === event.key;
    }

    // Named keys carry no character of their own, so they still say it.
    return shortcut.combo === event.key && (shortcut.shift ?? false) === event.shiftKey;
};

export const PaloShortcutProvider = ({ children }: { children: ReactNode }): ReactElement => {
    const shortcuts = useRef<PaloShortcut[]>([]);

    const register = useCallback((shortcut: PaloShortcut) => {
        // Last registered wins, so a mask can shadow a global key while it is
        // open without the global one having to know about it.
        shortcuts.current = [shortcut, ...shortcuts.current];

        return () => {
            shortcuts.current = shortcuts.current.filter(entry => entry !== shortcut);
        };
    }, []);

    const list = useCallback(
        () => shortcuts.current.filter(shortcut => !shortcut.disabled && !shortcut.hidden),
        []
    );

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            const plain = !event.metaKey && !event.ctrlKey && !event.altKey;

            if (plain && isEditable(event.target)) {
                return;
            }

            const shortcut = shortcuts.current.find(entry => !entry.disabled && matches(entry, event));

            if (!shortcut) {
                return;
            }

            event.preventDefault();
            shortcut.handler(event);
        };

        window.addEventListener('keydown', onKeyDown);

        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    const registry = useMemo(() => ({ register, list }), [register, list]);

    return <PaloShortcutContext.Provider value={registry}>{children}</PaloShortcutContext.Provider>;
};

/**
 * Registers one shortcut for as long as the component is mounted.
 *
 * `handler` may be recreated on every render; it is read through a ref, so the
 * registration itself does not churn.
 */
export const usePaloShortcut = (shortcut: PaloShortcut | undefined): void => {
    const registry = useContext(PaloShortcutContext);
    const handlerRef = useRef(shortcut?.handler);

    handlerRef.current = shortcut?.handler;

    const { combo, meta, shift, label, group, disabled, hidden } = shortcut ?? {};

    useEffect(() => {
        if (!registry || !combo || !label) {
            return;
        }

        return registry.register({
            combo,
            meta,
            shift,
            label,
            group,
            disabled,
            hidden,
            handler: event => handlerRef.current?.(event),
        });
    }, [registry, combo, meta, shift, label, group, disabled, hidden]);
};

/**
 * The hook as a component, for the class components that still make up the
 * document masks. One binding registers one shortcut, so the hook is called
 * exactly once per instance.
 */
export const PaloShortcutBinding = (shortcut: PaloShortcut): null => {
    usePaloShortcut(shortcut);

    return null;
};

export const usePaloShortcutList = (): PaloShortcut[] => {
    const registry = useContext(PaloShortcutContext);

    return registry ? registry.list() : [];
};
